import { LabeledFile } from "../../../Model/UIComponents.model";
import { useMemo } from "react";
import { ImageRulesOptions, MimeType } from "../../../Model/Products.model";

export const isAbsoluteResourceUrl = (url: string) =>
  /^https?:\/\//i.test(url) || url.startsWith("data:") || url.startsWith("blob:");

type UploadMode = "image" | "file" | "image+file";

const IMAGE_TYPES: MimeType[] = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
];

const FILE_TYPES: MimeType[] = ["application/pdf"];

export function useImageUploadRules(opts: ImageRulesOptions & { mode?: UploadMode }) {
  const {
    mode = "image",
    maxFiles,
    maxSizeMB,
    allowedTypes: allowedTypesFromCaller,
    requireAtLeastOne = true,
    enforceTotalSize = true,
    baseUrl,
    customMessages,
  } = opts;

  const allowedTypes = useMemo<MimeType[]>(() => {
    if (allowedTypesFromCaller?.length) return allowedTypesFromCaller as MimeType[];

    if (mode === "file") return FILE_TYPES;
    if (mode === "image+file") return [...IMAGE_TYPES, ...FILE_TYPES];
    return IMAGE_TYPES; // default "image"
  }, [allowedTypesFromCaller, mode]);

  const API_BASE_URL = useMemo(
    () => (baseUrl ?? process.env.REACT_APP_BASE_URL ?? "").replace(/\/+$/, ""),
    [baseUrl]
  );

  const buildImageSrc = useMemo(
    () => (imageUrl?: string | null) => {
      if (!imageUrl) return null;
      if (isAbsoluteResourceUrl(imageUrl)) return imageUrl;
      const normalized = imageUrl.startsWith("/") ? imageUrl : `/${imageUrl}`;
      return API_BASE_URL ? `${API_BASE_URL}${normalized}` : normalized;
    },
    [API_BASE_URL]
  );

  const label = useMemo(() => {
    if (mode === "file") return { singular: "file", plural: "files", short: "PDF" };
    if (mode === "image+file") return { singular: "file", plural: "files", short: "Images/PDF" };
    return { singular: "image", plural: "images", short: "Images" };
  }, [mode]);

  const imageRules = useMemo(
    () => ({
      validate: (fl: FileList | null | undefined) => {
        const arr = fl ? Array.from(fl) : [];

        if (requireAtLeastOne && arr.length === 0) {
          return (
            customMessages?.atLeastOne ??
            `At least one ${label.singular} is required`
          );
        }

        if (arr.length > maxFiles) {
          return (
            customMessages?.tooMany?.(maxFiles) ??
            `You can upload at most ${maxFiles} ${label.plural}`
          );
        }

        let total = 0;
        for (const f of arr) {
          if (!allowedTypes.includes(f.type as MimeType)) {
            const defaultBadType =
              mode === "file"
                ? "Only PDF files are allowed"
                : mode === "image+file"
                  ? "Only images (PNG, JPG, WEBP) or PDF are allowed"
                  : "Images must be PNG, JPG, or WEBP";

            return customMessages?.badType ?? defaultBadType;
          }

          if (f.size > maxSizeMB * 1024 * 1024) {
            return (
              customMessages?.tooLargePerFile?.(maxSizeMB) ??
              `Each ${label.singular} must be ≤ ${maxSizeMB}MB`
            );
          }

          total += f.size;
        }

        if (enforceTotalSize) {
          const maxTotalMB = maxFiles * maxSizeMB;
          if (total > maxTotalMB * 1024 * 1024) {
            return (
              customMessages?.tooLargeTotal?.(maxTotalMB) ??
              `Total ${label.short} size must be ≤ ${maxTotalMB}MB`
            );
          }
        }

        return true;
      },
    }),
    [
      allowedTypes,
      customMessages,
      enforceTotalSize,
      label.plural,
      label.short,
      label.singular,
      maxFiles,
      maxSizeMB,
      mode,
      requireAtLeastOne,
    ]
  );
  
  const filesRules = useMemo(
    () => ({
      validate: (files: LabeledFile[] | null | undefined) => {
        const arr = files ? files : [];

        if (requireAtLeastOne && arr.length === 0) {
          return (
            customMessages?.atLeastOne ??
            `At least one ${label.singular} is required`
          );
        }

        if (arr.length > maxFiles) {
          return (
            customMessages?.tooMany?.(maxFiles) ??
            `You can upload at most ${maxFiles} ${label.plural}`
          );
        }

        let total = 0;
        for (const f of arr) {
          // console.log({f});
          
          if (!allowedTypes.includes(f.file.type as MimeType)) {
            const defaultBadType =
              mode === "file"
                ? "Only PDF files are allowed"
                : mode === "image+file"
                  ? "Only images (PNG, JPG, WEBP) or PDF are allowed"
                  : "Images must be PNG, JPG, or WEBP";

            return customMessages?.badType ?? defaultBadType;
          }

          if (f.file.size > maxSizeMB * 1024 * 1024) {
            return (
              customMessages?.tooLargePerFile?.(maxSizeMB) ??
              `Each ${label.singular} must be ≤ ${maxSizeMB}MB`
            );
          }

          total += f.file.size;
        }
        
        if(files){
          const allValid =
          files.length > 0 &&
          files.every((file) => file.fileLabel.trim() !== "");
          if(!allValid){
            return(
              customMessages?.filesRequireLabels ?? 'Each file must have a label.'
            )
          }
        }

        if (enforceTotalSize) {
          const maxTotalMB = maxFiles * maxSizeMB;
          if (total > maxTotalMB * 1024 * 1024) {
            return (
              customMessages?.tooLargeTotal?.(maxTotalMB) ??
              `Total ${label.short} size must be ≤ ${maxTotalMB}MB`
            );
          }
        }

        return true;
      },
    }),
    [
      allowedTypes,
      customMessages,
      enforceTotalSize,
      label.plural,
      label.short,
      label.singular,
      maxFiles,
      maxSizeMB,
      mode,
      requireAtLeastOne,
    ]
  );

  const acceptAttr = allowedTypes.join(",");

  const helperText = useMemo(() => {
    if (mode === "file") return `PDF only • ≤ ${maxSizeMB}MB`;
    if (mode === "image+file")
      return `PNG, JPG, WEBP, or PDF • ≤ ${maxSizeMB}MB each`;
    return `PNG, JPG, or WEBP • ≤ ${maxSizeMB}MB each`;
  }, [mode, maxSizeMB]);

  return {
    imageRules,
    filesRules,
    helperText,
    acceptAttr,
    allowedTypes,
    maxFiles,
    maxSizeMB,
    buildImageSrc,
    isAbsoluteResourceUrl,
    API_BASE_URL,
  };
}

export default useImageUploadRules;
